import type { EmojiMartData } from "@emoji-mart/data";
import I18n from "@emoji-mart/data/i18n/en.json";
import Data from "@emoji-mart/data/sets/14/native.json";

export interface EmojiEntrySkin {
  native: string;
  unified: string;
  shortCode: string;
  shortCodeOmitTone: string;
  x?: number;
  y?: number;
}

export interface EmojiEntry {
  id: string;
  name: string;
  keywords: string[];
  aliases?: string[];
  skins: EmojiEntrySkin[];
}

export interface EmojiCategory {
  id: string;
  name: string;
  emojis: EmojiEntry[];
}

class EmojiIndex {
  private orderedEmojis: EmojiEntry[] = [];
  private emojiMap = new Map<string, EmojiEntry>();
  private natives = new Map<string, EmojiEntrySkin>();
  private skinToEmoji = new WeakMap<EmojiEntrySkin, EmojiEntry>();
  private mappedCategories: EmojiCategory[] = [];

  constructor(private readonly data: EmojiMartData) {
    this.buildIndex();
  }

  public get Categories() {
    return this.mappedCategories;
  }

  public get OrderedEmojis() {
    return this.orderedEmojis;
  }

  public findById(id: string): EmojiEntry | undefined {
    return this.emojiMap.get(id);
  }

  public findBySkin(skin: EmojiEntrySkin): EmojiEntry | undefined {
    return this.skinToEmoji.get(skin);
  }

  public findByNative(native: string): EmojiEntrySkin | undefined {
    return this.natives.get(native);
  }

  private buildIndex() {
    for (const key in this.data.emojis) {
      if (Object.prototype.hasOwnProperty.call(this.data.emojis, key)) {
        const emoji = this.data.emojis[key];
        if (!emoji) continue;

        const emojiEntry: EmojiEntry = {
          id: emoji.id,
          name: emoji.name || "",
          keywords: emoji.keywords,
          skins: [],
        };

        for (let i = 0; i < emoji.skins.length; i++) {
          const skin = emoji.skins[i]!;
          const skinCode = i === 0 ? "" : `:skin-tone-${i + 1}`;

          const skinEntry: EmojiEntrySkin = {
            native: skin.native,
            unified: skin.unified,
            shortCode: `:${emoji.id}::${skinCode}`,
            shortCodeOmitTone: `:${emoji.id}:`,
            x: skin.x,
            y: skin.y,
          };

          emojiEntry.skins.push(skinEntry);
          this.natives.set(skin.native, skinEntry);
          this.skinToEmoji.set(skinEntry, emojiEntry);
        }

        this.orderedEmojis.push(emojiEntry);
        this.emojiMap.set(emoji.id, emojiEntry);
      }
    }

    for (const alias in this.data.aliases) {
      if (Object.prototype.hasOwnProperty.call(this.data.aliases, alias)) {
        const emojiId = this.data.aliases[alias];
        if (!emojiId) continue;

        const emoji = this.emojiMap.get(emojiId);
        if (!emoji) continue;
        const aliases = emoji.aliases ?? (emoji.aliases = []);
        aliases.push(alias);
      }
    }

    this.mappedCategories = this.data.categories.map(category => {
      const id = category.id;
      const categoryName = (I18n.categories as Record<string, string>)[id];
      return {
        id,
        name: categoryName || id,
        emojis: category.emojis.map(e => this.emojiMap.get(e)).filter(Boolean) as EmojiEntry[],
      };
    });
  }
}

export const emojiIndex = new EmojiIndex(Data);
